import {
  ApiCitation,
  ApiQuestionnaire,
  ApiRetrievedReference,
} from "@/types/questionnaire/api"
import {
  Citation,
  Questionnaire,
  QuestionnaireResponsePair,
  QuestionnaireSection,
} from "@/types/questionnaire/models"
import { stripPreprocessedSuffix } from "@/utils/fileUtils"

export function mapApiQuestionnaireToQuestionnaire(
  data: ApiQuestionnaire,
): Questionnaire {
  if (!data.sections) {
    return {
      ...data,
      errorCode: data.errorCode ?? null,
      errorMessage: data.errorMessage ?? "",
      isRetryable: data.isRetryable ?? false,
      ingestionStatus: data.ingestionStatus ?? null,
      sections: [],
    }
  }

  const sections: QuestionnaireSection[] = []
  for (const apiSection of data.sections) {
    const responsePairs: QuestionnaireResponsePair[] = []

    for (const apiResponsePair of apiSection.responsePairs) {
      const citations = mapApiCitationsToCitations(apiResponsePair.citations)
      const lowConfidence =
        apiResponsePair.lowConfidence === null
          ? true
          : apiResponsePair.lowConfidence
      const responsePair: QuestionnaireResponsePair = {
        ...apiResponsePair,
        lowConfidence,
        citations,
      }
      responsePairs.push(responsePair)
    }

    const section = { ...apiSection, responsePairs }
    sections.push(section)
  }

  const questionnaire: Questionnaire = {
    ...data,
    errorCode: data.errorCode ?? null,
    errorMessage: data.errorMessage ?? "",
    isRetryable: data.isRetryable ?? false,
    ingestionStatus: data.ingestionStatus ?? null,
    sections,
  }
  return questionnaire
}

export function mapApiCitationsToCitations(
  apiCitations: ApiCitation[],
): Citation[] {
  if (!apiCitations[0] || !apiCitations[0].retrievedReferences) {
    return []
  }

  const retrievedReferences = apiCitations[0].retrievedReferences
  return retrievedReferences.map((reference: ApiRetrievedReference) => {
    const fileUri = reference.location.s3Location?.uri || ""
    const fileUriParts = fileUri.split("/")
    const fileName = stripPreprocessedSuffix(
      fileUriParts[Math.max(0, fileUriParts.length - 1)] || "",
    )

    const fileId = reference.metadata.documentId || ""

    const pageNumber =
      reference.metadata["x-amz-bedrock-kb-document-page-number"] || 0

    // Map bounding regions from API format
    const boundingRegions = reference.location.boundingRegions?.map((br) => ({
      pageNumber: br.pageNumber,
      polygon: br.polygon,
    }))

    const sourceType =
      reference.metadata.sourceType ?? reference.metadata.source_type

    return {
      sourceFileName: fileName,
      sourceFileId: fileId,
      sourceFilePage: pageNumber,
      sourceText: reference.content || undefined,
      ...(sourceType ? { sourceType } : {}),
      ...(boundingRegions?.length ? { boundingRegions } : {}),
    }
  })
}
